import { MenuItem, Select, SelectChangeEvent, Slider, Theme } from '@mui/material';
import { ClientComponent } from 'components/guardedComponent/ClientComponent';
import { FeatureComponent } from 'components/guardedComponent/FeatureComponent';
import { SettingsSection, SettingsSectionProps } from 'components/settings/SettingsSection';
import { PlaylistExportType } from 'hooks/usePlaylistExport';
import { useUser } from 'hooks/useUser';
import { Feature } from 'models/Feature';
import { MappingAutoMatchThreshold } from 'models/Mapping';
import { TrackBuyProvider } from 'models/Track';
import { useTranslation } from 'react-i18next';
import { SettingsField } from '../SettingsField';

export interface LibrarySettingsSectionProps extends SettingsSectionProps {}

export function LibrarySettingsSection(props: LibrarySettingsSectionProps) {
  const { t } = useTranslation();
  const { preferences, updatePreferences } = useUser();
  const autoMatchThresholdKeys = Object.values(MappingAutoMatchThreshold);
  const currentAutoMatchThresholdIndex = autoMatchThresholdKeys.indexOf(
    preferences.autoMatchThreshold
  );
  const maxAutoMatchThreshold = autoMatchThresholdKeys.length - 1;
  const currentThreshold = autoMatchThresholdKeys[currentAutoMatchThresholdIndex ?? 0];

  const getThresholdColor = (theme: Theme) => {
    switch (currentThreshold) {
      case MappingAutoMatchThreshold.Medium:
        return theme.palette.primary.main;
      case MappingAutoMatchThreshold.Exact:
        return theme.palette.error.main;
      case MappingAutoMatchThreshold.Low:
      case MappingAutoMatchThreshold.High:
      default:
        return theme.palette.warning.main;
    }
  };

  const handleDefaultPlaylistExportChange = (event: SelectChangeEvent) => {
    const value = event.target.value as PlaylistExportType;

    updatePreferences({
      defaultExportType: value,
    });
  };

  const handleDefaultBuyProviderChange = (event: SelectChangeEvent) => {
    const value = event.target.value as TrackBuyProvider;

    updatePreferences({
      defaultBuyProvider: value,
    });
  };

  const handleAutoMatchThresholdChange = (_: Event, value: number | number[]) => {
    const index = value as number;

    const key = autoMatchThresholdKeys[index];
    const autoMatchThreshold = MappingAutoMatchThreshold[key];

    updatePreferences({
      autoMatchThreshold: autoMatchThreshold,
    });
  };

  return (
    <SettingsSection title="Library" {...props}>
      <ClientComponent mode="overlay" showDownloadButton>
        <SettingsField
          label={t('playlistExport', 'Playlist Export')}
          description={t(
            'playlistExportDescription',
            'Default export file type. Most DJ Software support M3U files.'
          )}
        >
          <Select
            fullWidth
            value={preferences.defaultExportType}
            onChange={handleDefaultPlaylistExportChange}
          >
            <MenuItem key={PlaylistExportType.M3U} value={PlaylistExportType.M3U}>
              {t('exportPlaylistM3u', `M3U`, { type: PlaylistExportType.M3U })}
            </MenuItem>
            <MenuItem key={PlaylistExportType.CRATE} value={PlaylistExportType.CRATE}>
              {t('exportPlaylistCrate', `Serato Crate`, {
                type: PlaylistExportType.CRATE,
              })}
            </MenuItem>
            <MenuItem key={PlaylistExportType.CSV} value={PlaylistExportType.CSV}>
              {t('exportPlaylistCsv', `Excel/CSV`, { type: PlaylistExportType.CSV })}
            </MenuItem>
          </Select>
        </SettingsField>
        <SettingsField
          label={t('defaultBuyProvider', 'Default buy provider')}
          description={t('defaultBuyProviderDescription', 'Default provider to buy new tracks.')}
        >
          <Select
            fullWidth
            value={preferences.defaultBuyProvider}
            onChange={handleDefaultBuyProviderChange}
          >
            <MenuItem value={TrackBuyProvider.Apple}>{t('appleMusic', 'Apple Music')}</MenuItem>
            <FeatureComponent feature={Feature.DefaultBuyProvider}>
              <MenuItem value={TrackBuyProvider.DjCity}>{t('djcity', 'DJcity')}</MenuItem>
            </FeatureComponent>
            <FeatureComponent feature={Feature.DefaultBuyProvider}>
              <MenuItem value={TrackBuyProvider.Beatport}>{t('beatport', 'Beatport')}</MenuItem>
            </FeatureComponent>
          </Select>
        </SettingsField>
        <SettingsField
          // disabled
          label={t('autoMatchThreshold', 'Auto-match threshold')}
          description={t(
            'autoMatchThresholdDescription',
            'Minimum similarity required for the algorithm to automatically match a song.'
          )}
        >
          <Slider
            sx={(theme) => ({
              mt: 3,
              color: getThresholdColor(theme),
            })}
            onChange={handleAutoMatchThresholdChange}
            aria-label={t('autoMatchThreshold', 'Auto-match threshold')}
            value={currentAutoMatchThresholdIndex ?? 0}
            valueLabelDisplay="on"
            shiftStep={1}
            step={1}
            marks
            min={0}
            max={maxAutoMatchThreshold}
            valueLabelFormat={(value) => {
              const key = autoMatchThresholdKeys[value];
              switch (key) {
                case MappingAutoMatchThreshold.Low:
                  return t('autoMatchThresholdLow', 'Approximate');
                case MappingAutoMatchThreshold.Medium:
                  return t('autoMatchThresholdMedium', 'Recommended');
                case MappingAutoMatchThreshold.High:
                  return t('autoMatchThresholdHigh', 'Precise');
                case MappingAutoMatchThreshold.Exact:
                  return t('autoMatchThresholdExact', 'Exact');
                default:
                  return '';
              }
            }}
          />
        </SettingsField>
        {/* <SettingsField
          label={'DEV-TEST: Auto-match threshold'}
          description={'Zum Testen: Legt fest, wann ein Track automatisch gemapped wird'}
        >
          <Slider
            sx={{ mt: 3 }}
            onChange={(_: Event, value: number | number[]) => {
              updatePreferences({
                autoMatchThresholdTest: value as number,
              });
            }}
            aria-label={t('autoMatchThreshold', 'Auto-match threshold')}
            value={preferences.autoMatchThresholdTest ?? 0}
            valueLabelDisplay="on"
            shiftStep={0.05}
            step={0.05}
            marks
            min={0}
            max={2}
            // valueLabelFormat={(value) => {
            //   const key = autoMatchThresholdKeys[value];
            //   return t(`autoMatchThreshold.${key}`, key);
            // }}
          />
        </SettingsField> */}
        {/* <SettingsField
          label={'DEV-TEST: Find-match threshold'}
          description={
            'Zum Testen: Legt fest, wann ein Track "gefunden" wird. Sollte immer höher sein als Auto-match threshold'
          }
        >
          <Slider
            sx={{ mt: 3 }}
            onChange={(_: Event, value: number | number[]) => {
              updatePreferences({
                findMatchThresholdTest: value as number,
              });
            }}
            aria-label={t('autoMatchThreshold', 'Auto-match threshold')}
            value={preferences.findMatchThresholdTest ?? 0}
            valueLabelDisplay="on"
            shiftStep={0.05}
            step={0.05}
            marks
            min={0}
            max={2}
            // valueLabelFormat={(value) => {
            //   const key = autoMatchThresholdKeys[value];
            //   return t(`autoMatchThreshold.${key}`, key);
            // }}
          />
        </SettingsField> */}
      </ClientComponent>
    </SettingsSection>
  );
}
